/**
 * Autenticacion contra el SSO por Authorization Code + PKCE.
 *
 * La consola no tiene formulario de login propio: manda al usuario a la
 * pantalla unica del SSO. Esa pantalla es la que sostiene la cookie de sesion
 * compartida, y por eso entrar a una segunda aplicacion no vuelve a pedir
 * credenciales.
 */

import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type { AuthSession, IAuthService } from '@domain/services';
import { env } from '@infrastructure/config';

/** Claim donde la Lambda pre-token-generation deja los roles del usuario. */
const ROLES_CLAIM = 'roles';

interface SsoTokenProfile {
  email?: string;
  given_name?: string;
  family_name?: string;
  [ROLES_CLAIM]?: string[] | string;
}

export class OidcAuthService implements IAuthService {
  private readonly userManager: UserManager;
  private accessToken: string | null = null;

  constructor() {
    this.userManager = new UserManager({
      authority: env.auth.authority,
      client_id: env.auth.clientId,
      redirect_uri: env.auth.redirectUri,
      // Sin esto el iframe de renovacion carga la SPA entera y la pantalla
      // principal queda esperando para siempre.
      silent_redirect_uri: env.auth.silentRedirectUri,
      post_logout_redirect_uri: env.auth.postLogoutRedirectUri,
      response_type: 'code',
      scope: env.auth.scope,
      // sessionStorage: el token muere al cerrar la pestaña, y la sesion se
      // recupera en silencio desde la cookie del SSO.
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
      automaticSilentRenew: true,
      loadUserInfo: false,
    });
  }

  async login(): Promise<void> {
    await this.userManager.signinRedirect();
  }

  async completeLogin(): Promise<AuthSession> {
    const user = await this.userManager.signinRedirectCallback();
    return this.toSession(user);
  }

  /**
   * El logout tiene que matar la sesion del SSO, no solo el token local: si no,
   * el usuario "sale" y vuelve a entrar sin credenciales.
   *
   * El IdP propio implementa el `end_session_endpoint` del estandar; Cognito
   * no, y expone su `/logout` con otros parametros. Se usa el estandar cuando
   * existe y se cae al endpoint propietario cuando no.
   */
  async logout(): Promise<void> {
    const metadata = await this.userManager.metadataService.getMetadata();

    // El IdP propio implementa el cierre de sesion estandar.
    if (metadata.end_session_endpoint) {
      // El token local se borra antes del redirect: si no, al volver del cierre
      // la aplicacion se ve "logueada" con un token que ya no vale nada, hasta
      // que expire. Se conserva el id_token para identificar la sesion a cerrar.
      const user = await this.userManager.getUser();
      await this.userManager.removeUser();
      this.accessToken = null;

      await this.userManager.signoutRedirect({ id_token_hint: user?.id_token });
      return;
    }

    await this.userManager.removeUser();
    this.accessToken = null;

    const logoutUrl = new URL(env.auth.logoutPath, env.auth.loginDomain);
    logoutUrl.searchParams.set('client_id', env.auth.clientId);
    logoutUrl.searchParams.set('logout_uri', env.auth.postLogoutRedirectUri);

    window.location.assign(logoutUrl.toString());
  }

  async getSession(): Promise<AuthSession | null> {
    let user = await this.userManager.getUser();

    if (!user || user.expired) {
      user = await this.renewSilently();
    }

    if (!user) {
      this.accessToken = null;
      return null;
    }

    return this.toSession(user);
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private async renewSilently(): Promise<User | null> {
    try {
      return await this.userManager.signinSilent();
    } catch {
      return null;
    }
  }

  private toSession(user: User): AuthSession {
    this.accessToken = user.access_token;

    const profile = user.profile as SsoTokenProfile;
    const claimRoles = profile[ROLES_CLAIM];
    const roles = Array.isArray(claimRoles)
      ? claimRoles
      : typeof claimRoles === 'string'
        ? claimRoles.split(',').map(role => role.trim())
        : [];

    return {
      accessToken: user.access_token,
      expiresAt: (user.expires_at ?? 0) * 1000,
      email: profile.email ?? '',
      fullName: [profile.given_name, profile.family_name].filter(Boolean).join(' '),
      roles,
    };
  }
}
